import * as ng from 'angular';
import * as Core from '../Core';
import {CreditCardControlState} from "../user-controls/credit-card-control-state";


interface IEntity<T> extends ng.resource.IResource<T> {
    id: string;
    $update(): ng.IPromise<T>;
}

interface IAccountsResource<T> extends IEntity<T> {
    accountId: string;
}

export interface IProfile extends IAccountsResource<IProfile> {
    userId: string;
    frequentParkerAccountId: string;
    isPrimary: boolean;
    firstName: string;
    lastName: string;
    email: string;
    homePhone: string;
    mobilePhone: string;
    workPhone: string;
    fax: string;
    language: string;
    securityQuestion: ISecurityQuestion;
    recordVersion: string;
    isEnabled: boolean;
    username: string;
    passwordResetEmailWasSent: boolean;
}

export interface ISecurityQuestion extends IEntity<ISecurityQuestion> {

    text: string;
}

export interface IAccount extends IEntity<IAccount> {
    externalId: string;
    name: string;
    address1: string;
    address2: string;
    city: string;
    subdivision: string;
    postalCode: string;
    country: string;
    balance: number;
    forfeitureBalance: number;
    blocked: boolean;
    recordVersion: string;
    pendingSync: boolean;
    status: AccountStatus;
    accountType: number;
    primaryContact: IProfile;
    primaryCreditCard: ICreditCard;
    frequentParkerAccountId: string;
    frequentParkerNumber: string;
    isAMUF: boolean;
    isDecliningBalance: boolean;
    isReservedParking: boolean;
    fullAddress: string;
}

export interface ICreditCard extends IAccountsResource<ICreditCard> {
    creditCardType: string;
    expirationMonth: string;
    expirationYear: string;
    isPrimary: boolean;
    maskedCreditCard: string;
    token: string;
    recordVersion: string;
}

export interface ICreditCardOperationTicket extends IEntity<ICreditCardOperationTicket> {
    ticket: string;
}

export interface IReCaptchaValidation extends IEntity<IReCaptchaValidation> {
    isValid: boolean;
}

export interface IParkingMedia extends IAccountsResource<IParkingMedia> {
    userProfile: IProfile;
    mediaIdentifier: string;
    description: string;
    effectiveDate: string;
    expirationDate: string;
    blocked: boolean;
    disabled: boolean;
    disabledDateTime: string;
    disabledReasonId: number;
    recordVersion: string;
    externalId: string;
    accountExternalId: string;
    redeem: boolean;
}

export interface IClaim extends IEntity<IClaim> {
    claimType: string;
    claimValue: string;
    isDeleted: string;
}

export interface ILogin extends IEntity<ILogin> {
    loginProvider: string;
    providerKey: string;
}

export interface IUser extends IEntity<IUser> {
    username: string;
    firstName: string;
    lastName: string;
    password?: string;
    isEnabled: boolean;
    claims: Array<IClaim>;
    logins: Array<ILogin>;
    securityAnswer: string;

}

export interface ICountry extends IEntity<ICountry> {
    officialName: string;
    name: string;
    code: number;
    abbrevShort: string;
    abbrevLong: string;
    subdivisionDescription: string;
    subdivisionRequired: boolean;
    subdivisionExactMatch: boolean;
}

export interface IFrequentParkerAccount extends IAccountsResource<IFrequentParkerAccount> {
    userProfileId: string;
    number: string;
    usePointsNextVisit:boolean;
    automaticRedemption: boolean;
}

export interface IParkingArea extends IEntity<IParkingArea> {
    name: string;
    initialBalance: number;
    spacesAvailable: boolean;
    forfeitureMonthlyAmount: number;
    description: string;
    dailyRate: number;
    membershipCost: number;
    isDecliningBalance: boolean;
    externalId: string;
}

export interface IParkingMediaRequest extends IAccountsResource<IParkingMediaRequest> {
    parkingMediaTypeId: string;
    userProfileId: string;
    requestDateTime: string;
    statusId: number;
    quantity: number;
    /*
    Comma separated list of user entered media identifiers.
    * */
    mediaIdentifier: string;
    recordVersion: string;
    replaceParkingMediaId: string;
    externalId: string;
    accountExternalId: string;
    parkingMediaRequestId: number;
    replaceParkingMediaMediaIdentifier: string;
}

export interface IParkingMediaType extends IEntity<IParkingMediaType> {
    price: number;
    description: string;
    externalId: string;
    supportsUserEntry: boolean;
    identifierValidationExpression: string;
    identifierValidationErrorMessage: string;
    identifierValidationWatermark: string;
    identifierValidationTooltip: string;
}

export interface ISubdivision extends IEntity<ISubdivision> {
    officialName: string;
    name: string;
    code: number;
    abbrevShort: string;
    abbrevLong: string;
    subdivisionDescription: string;
    subdivisionRequired: boolean;
    subdivisionExactMatch: boolean;
}

export interface IAccountRequest extends IEntity<IAccountRequest> {
    accountRequestId: number;
    accountType: string;
    name: string;
    address1: string;
    address2: string;
    city: string;
    country: string;
    subdivision: string;
    postalCode: string;
    firstName: string;
    lastName: string;
    email: string;
    homePhone: string;
    mobilePhone: string;
    workPhone: string;
    fax: string;
    language: string;
    username: string;
    password: string;
    confirm: string;
    securityQuestionId: string;
    securityAnswer: string;
    preferredParkingAreaId: string;
    alternateParkingAreaId: string;
    requestDateTime: string;
    isReserveParkingRequest: boolean;
    isFrequentParkerRequest: boolean;
    creditCardType: string;
    expirationMonth: string;
    expirationYear: string;
    maskedCreditCard: string;
    token: string;
    parkingMediaRequests: Array<IParkingMediaRequest>
    parkingMediaType: number;
    accountStatus: AccountStatus;
    accountDeniedMessage: string;
    recordVersion: string;
    accountId: string;
    frequentParkerNumber: string;
}

export interface IAccountActivity extends IAccountsResource<IAccountActivity> {
    date: string;  
    firstName: string;
    lastName: string;
    MediaIdentifier: string;
    ParkingMediaType: string;
    mediaIdentifier: string;
    description: string;
    entryDateTime: string;
    exitDateTime: string;
    amount: number;
    beginBalance: number;
    endBalance: number;
    externalId: string;
    beginForfeitureBalance: number;
    endForfeitureBalance: number;
    accountExternalId: string;
    transactionType: number;
    credit: null;
    debit: null;
    parkHouseId?: number;
    parkHouseName?: string;
}

export interface IFrequentParkerActivity extends IEntity<IFrequentParkerActivity> {
    frequentParkerAccountId: string;
    accountActivityId: string;
    activityDateTime: string;
    activityType: number;
    points: number;
    beginPointBalance: number;
    endPointBalance: number;
    status: number;
    isDeleted: boolean;
    parkHouseId?: number;
    parkHouseName: string;
}

export interface IMessage extends IEntity<IMessage> {
    externalId: number;
    toType: number;
    accountRequestId: string;
    effectiveDate: string;
    expirationDate: string;
    subject: string;
    body: string;
    accountIds: Array<String>;
}

export interface IModelState extends Map<string, Array<string>> {

}

export interface IKeyValuePair {
    key: string;
    value: any;
}

export interface IPasswordResetRequest {
    password: string;
    resetToken: string;
}

export interface ISecurityQuestionAnswer {
    securityAnswer: string;
}

//export interface ISecurityQuestion {
//    id: string
//}

export interface IChangePasswordRequest {
    newPassword: string;
    currentPassword: string;
}

export interface IThemeSettings {
    defaultThemeName: string;
    themes: Array<ITheme>;
    customPalettes: Array<IPalette>;
}

export interface ITheme {
    name: string
    primaryPalette: IPaletteDefinition;
    accentPalette: IPaletteDefinition;
}

export interface IPaletteDefinition {
    paletteName: string;
    overrideMap: IIndexer
}

export interface IPalette {
    name: string;
    settings: IIndexer
}

interface IIndexer {
    [name: string]: Object
}


export interface ISettingsBase extends IEntity<ISettingsBase> {
    key: SettingsKeys;
    recordVersion: string;
}
export interface ISettings<T> extends ISettingsBase {
    settings: T;
}

export enum SettingsKeys {
    Undefined = 0,
    FrequentParker = 1,
    ReservedParking = 2,
    ContentSettings = 3,
    CreditCard = 4,
    SiteTheme = 5
}

export interface IFrequentParkerSettings {
    pointCalculatorFactory: string;
    redemptionCalculatorFactory: string;
    redemptionPolicyFactory: string;
    redemptionCalculators: Object;
    redemptionPolicies: Object;
    accrualCalculators: Object;
    defaultAccrualCalculator: IAccrualCalculator;
    defaultRedemptionCalculator: IDefaultRedemptionCalculator;
    enabled: boolean;
    schedulingBasis: SchedulingBasisType;
    promotionSettings: Array<IPromotionSettings>;
    parkHouses: Array<IParkHouse>;
    groups: Array<IPromotionGroup>;
    enableFppNumberEntry: boolean;
    requireFppNumberEntry: boolean;
}

export enum SchedulingBasisType {
    Undefined = 0,
    EntryTime = 1,
    ExitTime = 2
}

interface IAccrualCalculator {
    calculatorType: number;
    dollarToPointRatio: number;
    order: number;
}

interface IDefaultRedemptionCalculator {
    calculatorType: string;
    pointToDollarRatio: string;
    discountVendorId: string;
    discountProductId: string;
    order: string;
}

export interface IReservedParkingSettings {
    enabled: boolean;
    membershipFeeSettings: IMembershipFeeSettings;
}

interface IMembershipFeeSettings {
    rechargeDate: string;
    enabled: boolean;
    calculatorType: string;
}

export interface IContentSettings {
    menuButton: IMenuButton;
    headerLogo: ILogo;
    footerLogo: ILogo;
    contactInfo: IContactInfo;
}

interface IMenuButton {
    label: string;
    hyperlink: string;
}

interface ILogo {
    src: string;
    altText: string;
}

interface IContactInfo {
    phoneNumber: string;
}

export interface ICreditCardSettings {
    newTokenUrl: string;
    redirectHtml: string;
    redirectCss: string;
    creditCardServiceAppId: string;
}

export interface  IReCaptchaSettings {
    isEnabled: boolean;
    reCaptcha2PublicKey: string;
    reCaptcha3PublicKey: string;
}

export interface ISiteThemeSettings {
    themes: Object;
    customPalettes: Object;
}

export interface ILanguageSettings {
    languages: Array<Core.ILanguage>;
}

export enum ContentTypeSettingKeys {
    Test = -1,
    FAQ = 1,
    Footer = 2,
    Terms = 3,
    MediaRequest = 4,
    AccountDenied = 5,
    AccountApproved = 6,
    RequestReceived = 7,
    PasswordChanged = 8,
    PasswordReset = 9,
    ProfileUpdate = 10
}

//TODO: Type needs Capatilized
export enum contentGroupType {
    Page = 1,
    Email = 2,
    Block = 3
}

export interface IContentTemplate extends IEntity<IContentTemplate> {
    contentTemplateId?: number;
    key?: ContentTypeSettingKeys;
    languageCode?: string;
    content?: string;
    subject?: string;
    isNew: boolean;
}


export interface ITemplateToken extends IEntity<ITemplateToken> {
    templateTokensId: number;
    uiToken: string;
    razorToken: string;
}

export interface IContentTypeSettings extends IEntity<IContentTypeSettings> {
    contentTypeSettingsId: number;
    maxContentLength: number;
    key: ContentTypeSettingKeys;
    groupType: contentGroupType;
    display: string;
    templateTokens: Array<ITemplateToken>;
}

export interface ICriteria {
    sort: Array<string>;
    sortDirection: string;
    sortBy: string;
    pageSize: number;
    pageNumber: number;
    keywords: string;
}

export interface IAccountCriteria extends ICriteria {
    name: string;
    city: string;
    subdivision: string;
    postalCode: string;
    email: string;
    phoneNumber: string;
    accountRuntimeIds: Array<string>;
    externalIds: Array<string>;
    isDeleted: boolean;
    status: number;
}

export interface IAccountActivityCriteria extends ICriteria {
    accountId: string;
    startDate: string;
    endDate: string;
    minAmount: number;
    maxAmount: number;
    transactionType: number;   
    mediaIdentifier: string;
}

export interface IFrequentParkerActivityCriteria extends ICriteria {
    frequentParkerAccountId: string;
    startDate: string;
    endDate: string;
    activityType: number;   
    isPending: boolean;
    includeDeleted: boolean;
}

export interface IUserCriteria extends ICriteria {
    loginProvider: string;
    providerKey: string;
    userName: string;
}

export interface IProfileCriteria extends ICriteria {
    accountId: string;
    isPrimary: boolean;
    keywords: string;
    accountRuntimeIds: Array<string>;
    emailIds: Array<string>;
    isAdmin: boolean;
    isEnabled: boolean;
    userName: string;
}

export interface IAccountRequestCriteria extends ICriteria {
    accountId: string;
    accountRequestRuntimeIds: Array<string>;
    accountRequestIds: Array<number>;
    accountIds: Array<string>;
    isReserveParkingRequest: boolean;
    isFrequentParkerRequest: boolean;
    accountStatus: AccountStatus;
    isPendingApproval: boolean;
}

export interface INameValuePair {
    name: string;
    value: Object;
}

export interface IAdjustment {
    currencyType: string;
    creditAmount: number;
    debitAmount: number;
}

export interface IUserPasswordResetRequest {
    baseUri: string;
    username: string;
    challengeSecret: string;
}

export enum AccountStatus {
    Undefined = 0,
    PendingActivation = 1,
    PendingApproval = 2,
    Active = 3,
    Deactivated = 4,
    AccountDenied = 5
}

export interface IFrequentParkerAccountSummary extends ng.resource.IResource<IFrequentParkerAccountSummary> {
    totalAccounts: IDashboardActiveInactiveKpiResponse;
    currentMonthAwarded: IDashboardPointKpiResponse;
    lastMonthAwarded: IDashboardPointKpiResponse;
    currentYearAwarded: IDashboardPointKpiResponse;
    lastYearAwarded: IDashboardPointKpiResponse;
    signUpCurrentMonth: IDashboardCountKpiResponse;
    signUpLastMonth: IDashboardCountKpiResponse;
    signUpCurrentYear: IDashboardCountKpiResponse;
    signUpLastYear: IDashboardCountKpiResponse;
    currentMonthRedeemed: IDashboardPointKpiResponse;
    lastMonthRedeemed: IDashboardPointKpiResponse;
    currentYearRedeemed: IDashboardPointKpiResponse;
    lastYearRedeemed: IDashboardPointKpiResponse;
}



export interface IDashboardKpiRequest {
    startDate: string;
    endDate: string;
    key: DateRanges;
}

export interface IDashboardKpiResponse extends ng.resource.IResource<IDashboardKpiResponse> {
    startDate: string;
    endDate: string;
    key: DateRanges;
}

export interface IDashboardPointKpiResponse extends IDashboardKpiResponse {
    dollars: number;
    points: number;
}

export interface IDashboardCountKpiResponse extends IDashboardKpiResponse {
    count: number;
}

export interface IDashboardActiveInactiveKpiResponse extends IDashboardKpiResponse {
    active: number;
    inactive: number;
}

export class DashboardKPIs {
    public static PointsRedeemed = "PointsRedeemed";
    public static PointsAwarded = "PointsAwarded";
    public static SignUp = "SignUp";
    public static ActiveInactive = "ActiveInactive";
}

export enum DateRanges {
    Undefined = 0,
    CurrentMonth = 1,
    LastMonth = 2,
    CurrentYear = 3,
    LastYear = 4,
    Custom = 5,
    All = 6
}

export interface IPromotionSettings {
    id: string;
    name: string;
    isDefault: boolean;
    isActive: boolean;
    groups: Array<IPromotionGroup>;
    parkHouses: Array<IParkHouse>;
    pointAccruedPerDollar: number;
    pointsRedeemedPerDollar: number;
    minPointRedemption: number;
    pointRedemptionIncrements: number;
    schedule: ISchedule;
    isNew: boolean;
}

export interface IPromotionGroup {
    id: string;
    name: string;
    isEveryone: boolean;
    enabled: boolean;
}

export interface IParkHouse {
    id: number;
    parkHouseId: number;
    name: string;
    isEveryone: boolean;
    enabled: boolean;
    externalId: string;
}

export interface ISchedule {
    activeTimes: Array<ICustomActiveTime>;
    scheduleType: ScheduleTypeEnum;
    allDay: boolean;
    startTime: string;
    endTime: string;
}

export interface ICustomActiveTime {
    enabled: boolean;
    allDay: boolean;
    startTime: string;
    endTime: string;
    dayOfWeek: DayOfWeek
}

export enum ScheduleTypeEnum {
    All = 1,
    Weekdays = 2,
    Weekends = 3,
    Custom = 4
}

export enum DayOfWeek {
    Sunday = 0,
    Monday = 1,
    Tuesday = 2,
    Wednesday = 3,
    Thursday = 4,
    Friday = 5,
    Saturday = 6
}

export interface IGeneralSettings {
    phoneNumber: string;
    email: string;
    receiveCreditCardUpdateEmails: boolean;
    allowBusinessAccounts: boolean;
}


// WEBPACK FOOTER //
// ./node_modules/source-map-loader!./app/data/interfaces.ts